/**
 * Speech Store
 * Zustand store for speech/TTS settings with localStorage persistence
 * Maintains backward compatibility with vanilla JS localStorage keys
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SpeechSettings } from '../types'
import { STORAGE_KEYS } from '@config/constants'
import { SPEECH_CONSTANTS, sanitizeOpenAiVoice, sanitizeOpenAiModel } from '@config/speech'

export interface SpeechStore {
  speech: SpeechSettings
  updateApiKey: (key: string) => void
  updateVoice: (voice: SpeechSettings['voice']) => void
  updateModel: (model: SpeechSettings['model']) => void
  toggleEnabled: () => void
  toggleListenEnabled: () => void
  toggleListenPreferBrowser: () => void
  toggleUseProxy: () => void
  updateSpeech: (updates: Partial<SpeechSettings>) => void
  resetSpeech: () => void
}

export const DEFAULT_SPEECH: SpeechSettings = {
  key: '',
  voice: SPEECH_CONSTANTS.DEFAULT_OPENAI_VOICE,
  model: SPEECH_CONSTANTS.DEFAULT_OPENAI_MODEL,
  enabled: true, // Enabled by default with proxy mode
  listenEnabled: false,
  listenPreferBrowser: false,
  useProxy: true, // Default to using the Cloudflare Worker proxy
}

export function sanitizeLoadedSpeech(stored: unknown): SpeechSettings {
  if (!stored || typeof stored !== 'object') {
    return { ...DEFAULT_SPEECH }
  }

  const parsed = stored as Partial<SpeechSettings>

  const key = typeof parsed.key === 'string' ? parsed.key : ''
  const rawVoice =
    typeof parsed.voice === 'string' ? parsed.voice : SPEECH_CONSTANTS.DEFAULT_OPENAI_VOICE
  const voice = sanitizeOpenAiVoice(rawVoice)
  const rawModel =
    typeof parsed.model === 'string' ? parsed.model : SPEECH_CONSTANTS.DEFAULT_OPENAI_MODEL
  const model = sanitizeOpenAiModel(rawModel)
  // useProxy defaults to true for new users
  const useProxy = parsed.useProxy !== undefined ? Boolean(parsed.useProxy) : true
  // enabled: true if using proxy, or if has API key
  const enabled = useProxy || (Boolean(parsed.enabled) && key.length > 0)
  const listenEnabled = Boolean(parsed.listenEnabled)
  const listenPreferBrowser = Boolean(parsed.listenPreferBrowser)

  return { key, voice, model, enabled, listenEnabled, listenPreferBrowser, useProxy }
}

export const useSpeechStore = create<SpeechStore>()(
  persist(
    (set) => ({
      speech: { ...DEFAULT_SPEECH },

      updateApiKey: (key) =>
        set((state) => ({
          speech: {
            ...state.speech,
            key,
            enabled: key.length > 0,
          },
        })),

      updateVoice: (voice) =>
        set((state) => ({
          speech: { ...state.speech, voice },
        })),

      updateModel: (model) =>
        set((state) => ({
          speech: { ...state.speech, model },
        })),

      toggleEnabled: () =>
        set((state) => ({
          speech: {
            ...state.speech,
            enabled: !state.speech.enabled && state.speech.key.length > 0,
          },
        })),

      toggleListenEnabled: () =>
        set((state) => ({
          speech: {
            ...state.speech,
            listenEnabled: !state.speech.listenEnabled,
          },
        })),

      toggleListenPreferBrowser: () =>
        set((state) => ({
          speech: {
            ...state.speech,
            listenPreferBrowser: !state.speech.listenPreferBrowser,
          },
        })),

      toggleUseProxy: () =>
        set((state) => {
          const newUseProxy = !state.speech.useProxy
          return {
            speech: {
              ...state.speech,
              useProxy: newUseProxy,
              // When switching modes, update enabled status
              enabled: newUseProxy || state.speech.key.length > 0,
            },
          }
        }),

      updateSpeech: (updates) =>
        set((state) => ({
          speech: { ...state.speech, ...updates },
        })),

      resetSpeech: () =>
        set({
          speech: { ...DEFAULT_SPEECH },
        }),
    }),
    {
      name: STORAGE_KEYS.speech,
      partialize: (state) => ({
        speech: {
          key: state.speech.key || '',
          voice: state.speech.voice,
          model: state.speech.model,
          enabled: Boolean(state.speech.useProxy || (state.speech.enabled && state.speech.key)),
          listenEnabled: Boolean(state.speech.listenEnabled),
          listenPreferBrowser: Boolean(state.speech.listenPreferBrowser),
          useProxy: Boolean(state.speech.useProxy),
        },
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Sanitize speech settings on load
          const stored = localStorage.getItem(STORAGE_KEYS.speech)
          if (stored) {
            try {
              const parsed = JSON.parse(stored)
              const sanitized = sanitizeLoadedSpeech(parsed.state?.speech || parsed)
              state.speech = sanitized
            } catch (error) {
              console.warn('Failed to parse stored speech settings:', error)
            }
          }
        }
      },
    }
  )
)
